import React, { useState, useEffect } from 'react';
import { IoSquare } from 'react-icons/io5';
import SubGroupList from './SubGroupList';
import PropTypes from 'prop-types';
import useModal from './useModal';
import useOrganizationSearch from './useOrganizationSearch';
import HighlightText from './HighlightText';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import Routes from 'utils/routes.js.erb';
import { authHeaders } from 'actions';
import { errorMessage, customMessage } from 'actions/internal';
import { connect } from 'react-redux';


const GroupItem = ({ group, groupIdx, onCustomMessage, onErrorMessage }) => {
  const [userCollapsed, setUserCollapsed] = useState(null);
  const { openModal } = useModal();
  const { results, activeQuery } = useOrganizationSearch();
  // Force open only if this group is an ancestor of a matched subgroup/cohort
  // A direct match on the group name doesn't require expanding — the highlight is enough
  const forceExpanded = !!(results && results.ancestorGroupIds.has(group.id));
  // userCollapsed: null = user hasn't toggled yet (use defaults), true/false = explicit user choice
  const expanded = userCollapsed !== null ? !userCollapsed : (forceExpanded || groupIdx === 0);
  const queryClient = useQueryClient();

  // Reset user toggle when search is cleared
  useEffect(() => {
    if (!results) setUserCollapsed(null);
  }, [results]);

  const remove = useMutation({
    mutationFn: (group) => {
      return axios.delete(Routes.api_dashboard_administration_organizations_destroy_group_path({ group_id: group.id }), { headers: authHeaders() })
    },
    onSuccess: () => {
      onCustomMessage('Group deleted successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['administration', 'organization', 'groups'] })
    },
    onError: (err) => {
      if (axios.isAxiosError(err)) {
        onErrorMessage(Object.values(err.response.data.errors).flat().join(', '));
      } else {
        onErrorMessage('Oops! Something went wrong. Contact support for help.');
      }
    },
  })

  const isMatch = !!(results && results.matchedGroupIds.has(group.id));

  return (
    <>
      <div className={`row-cell${isMatch ? ' row-cell--matched' : ''}`}>
        <span className='cell expand level1' onClick={() => setUserCollapsed(expanded)}>
          <IoSquare style={{color: 'purple'}} />
          {expanded ? (<i className="fa fa-chevron-down"></i>) : (<i className="fa fa-chevron-right"></i>)}
        </span>
        <span className='cell id'>{group.id}</span>
        <span className='cell name'>
          <HighlightText text={group.name} query={activeQuery} />
          {group.can_edit &&(
            <a className="edit" onClick={() => openModal('group', group)}>
              <i className="fa fa-pencil"></i>
              &nbsp;
              Edit
            </a>
          )}
          {group.can_delete && (
            <a className="remove" onClick={() => window.confirm('Are you sure you want to delete this group?') && remove.mutate(group)}>
              <i className="fa fa-trash"></i>
              &nbsp;
              Remove
            </a>
          )}
        </span>
      </div>
      {expanded && (<SubGroupList group={group} groupIdx={groupIdx} />)}
    </>
  );
}

GroupItem.propTypes = {
  group: PropTypes.object.isRequired,
  groupIdx: PropTypes.number.isRequired,
  onErrorMessage: PropTypes.func.isRequired,
  onCustomMessage: PropTypes.func.isRequired,
}

const mapStateToProps = () => {
  return {}
}

const mapDispatchToProps = (dispatch) => {
  return {
    onErrorMessage: (message) => {
      dispatch(errorMessage(message))
    },
    onCustomMessage: (message, type) => {
      dispatch(customMessage(message, type))
    },
  }
};

export default connect(mapStateToProps, mapDispatchToProps)(GroupItem);

